'use client';

import {
  EXPERIENCE_RANGES,
  FEE_OPTIONS,
  type ClientType,
  type ExperienceRangeKey,
  type FeeOptionKey,
  type Service,
} from '@ria-client/advisor-search/constants';
import { Button } from '@ria-client/tw-shadcn/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@ria-client/tw-shadcn/components/dialog';
import { useEffect, useRef, useState, type RefObject } from 'react';

import { CloseIcon } from '../icons';
import { FilterPillGroup } from './FilterPillGroup';

export type FilterValues = {
  services: ReadonlyArray<Service>;
  fees: ReadonlyArray<FeeOptionKey>;
  clientTypes: ReadonlyArray<ClientType>;
  experience: ReadonlyArray<ExperienceRangeKey>;
};

export type FiltersDialogProps = {
  open: boolean;
  onClose: () => void;
  initialValues: FilterValues;
  onApply: (values: FilterValues) => void;
  isLoading?: boolean;
  triggerRef?: RefObject<HTMLButtonElement | null>;
};

const SERVICE_OPTIONS: ReadonlyArray<{ value: Service; label: string }> = [
  { value: 'Financial Planning', label: 'Financial Planning' },
  { value: 'Portfolio Management', label: 'Portfolio Management' },
];
const FEE_PILL_OPTIONS = FEE_OPTIONS.filter(fee => fee.key !== 'other').map(fee => ({
  value: fee.key,
  label: fee.label,
}));
const CLIENT_TYPE_OPTIONS: ReadonlyArray<{ value: ClientType; label: string }> = [
  { value: 'Individuals', label: 'Individuals' },
  { value: 'High-Net-Worth Individuals', label: 'High-Net-Worth Individuals' },
];
const EXPERIENCE_OPTIONS = EXPERIENCE_RANGES.map(range => ({
  value: range.key,
  label: range.label,
}));

export function FiltersDialog({
  open,
  onClose,
  initialValues,
  onApply,
  isLoading = false,
  triggerRef,
}: FiltersDialogProps) {
  const [draft, setDraft] = useState<FilterValues>(initialValues);
  const initialValuesKey = JSON.stringify([
    initialValues.services,
    initialValues.fees,
    initialValues.clientTypes,
    initialValues.experience,
  ]);
  const previousInitialValuesKey = useRef(initialValuesKey);
  const wasOpen = useRef(open);

  useEffect(() => {
    const shouldReset =
      open && (!wasOpen.current || previousInitialValuesKey.current !== initialValuesKey);
    wasOpen.current = open;
    previousInitialValuesKey.current = initialValuesKey;
    if (shouldReset) setDraft(initialValues);
  }, [open, initialValues, initialValuesKey]);

  function handleClearAll() {
    setDraft({ services: [], fees: [], clientTypes: [], experience: [] });
  }

  function handleApply() {
    onApply(draft);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={nextOpen => !nextOpen && onClose()}>
      <DialogContent
        showCloseButton={false}
        onCloseAutoFocus={event => {
          if (!triggerRef?.current) return;
          event.preventDefault();
          triggerRef.current.focus();
        }}
        className="grid h-dvh grid-rows-[auto_1fr_auto] p-0 sm:h-auto sm:max-h-[calc(100dvh-64px)]"
      >
        <header className="flex items-center justify-between px-4 py-3">
          <DialogTitle className="typography-text-large-bold">Filters</DialogTitle>
          <DialogDescription className="sr-only">Narrow your advisor results.</DialogDescription>
          <DialogClose asChild>
            <Button variant="ghost" size="icon" aria-label="Close filters">
              <CloseIcon />
            </Button>
          </DialogClose>
        </header>
        <div className="flex flex-col gap-4 overflow-y-auto border-y border-border px-4 py-4">
          <FilterPillGroup
            legend="Services"
            options={SERVICE_OPTIONS}
            selected={draft.services}
            onToggle={value =>
              setDraft(current => ({
                ...current,
                services: toggle(current.services, value),
              }))
            }
          />
          <FilterPillGroup
            legend="Compensation"
            options={FEE_PILL_OPTIONS}
            selected={draft.fees}
            onToggle={value =>
              setDraft(current => ({ ...current, fees: toggle(current.fees, value) }))
            }
          />
          <FilterPillGroup
            legend="Client Type"
            options={CLIENT_TYPE_OPTIONS}
            selected={draft.clientTypes}
            onToggle={value =>
              setDraft(current => ({ ...current, clientTypes: toggle(current.clientTypes, value) }))
            }
          />
          <FilterPillGroup
            legend="Firm Age"
            options={EXPERIENCE_OPTIONS}
            selected={draft.experience}
            onToggle={value =>
              setDraft(current => ({ ...current, experience: toggle(current.experience, value) }))
            }
          />
        </div>
        <footer className="flex items-center justify-between px-4 py-2">
          <Button
            variant="ghost"
            fullWidth={false}
            onClick={handleClearAll}
            className="font-semibold tracking-normal normal-case"
          >
            Clear all filters
          </Button>
          <Button
            fullWidth={false}
            disabled={isLoading}
            onClick={handleApply}
            className="rounded-full px-6 tracking-normal normal-case"
          >
            Show advisors
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

function toggle<TValue>(values: ReadonlyArray<TValue>, value: TValue): TValue[] {
  return values.includes(value) ? values.filter(current => current !== value) : [...values, value];
}
